<script lang="ts" setup>
import { Modal } from "@/components";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useCreateSubfolderMutation } from "@/graphql/operations";
const props = defineProps<{ active: boolean; id: string }>();
const emit = defineEmits(["close", "success"]);
const name = ref("");
const input = ref<HTMLInputElement>();
const submitting = ref(false);
const failed = ref(false);
const trimmedName = computed(() => name.value.trim());
let unmounted = false;
onBeforeUnmount(() => {
  unmounted = true;
});
onMounted(() => input.value?.focus());
const close = () => {
  if (!submitting.value) emit("close");
};
const { executeMutation } = useCreateSubfolderMutation();
const submit = async () => {
  if (!trimmedName.value || submitting.value) return;
  submitting.value = true;
  failed.value = false;
  try {
    const result = await executeMutation({
      parentId: props.id,
      name: trimmedName.value,
    });
    if (unmounted) return;
    const id =
      result.data?.createWorkspaceAndJoin?.result?.createdWorkspace?.id;
    if (result.error || !id) {
      failed.value = true;
      return;
    }
    emit("success", id);
    emit("close");
  } catch {
    if (!unmounted) failed.value = true;
  } finally {
    submitting.value = false;
  }
};
</script>
<template>
  <Modal @close="close" :active="active" :close-disabled="submitting">
    <template #header>{{
      $t("workspaceDetail.createSubfolder.modalHeader")
    }}</template>
    <template #body>
      <input
        type="text"
        class="input"
        ref="input"
        v-model="name"
        :disabled="submitting"
      />
      <p v-if="failed" role="alert" class="has-text-danger">
        {{ $t("error") }}
      </p>
    </template>
    <template #footer>
      <div class="buttons">
        <button
          class="button primary"
          :class="{ disabled: !trimmedName || submitting }"
          :disabled="!trimmedName || submitting"
          @click="submit"
        >
          {{ $t("submit") }}
        </button>
        <button class="button secondary" :disabled="submitting" @click="close">
          {{ $t("cancel") }}
        </button>
      </div>
    </template>
  </Modal>
</template>
